import { Pencil, Trash2, User } from 'lucide-react';
import type { Contact } from '../../types/contact';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
} from '../ui';

interface ContactListProps {
  contacts: Contact[];
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
  onSelectContact: (contact: Contact, isSelected: boolean) => void;
  selectedContacts: number[];
  isLoading?: boolean;
}

function ContactList({
  contacts,
  onEdit,
  onDelete,
  onSelectContact,
  selectedContacts,
  isLoading,
}: ContactListProps) {
  const getChannelBadge = (channel: string) => {
    const variants: Record<string, string> = {
      whatsapp: 'success',
      instagram: 'info',
      telegram: 'info',
      facebook: 'info',
      email: 'warning',
      other: 'default',
    };

    return <Badge variant={variants[channel] || 'default'}>{channel}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <EmptyState
        icon={<User className="h-12 w-12" />}
        title="Nenhum contato encontrado"
        description="Adicione seu primeiro contato ou importe contatos para começar"
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                onChange={e => {
                  // Select or deselect all contacts
                  contacts.forEach(contact => {
                    onSelectContact(contact, e.target.checked);
                  });
                }}
                checked={selectedContacts.length === contacts.length && contacts.length > 0}
              />
            </TableHead>
            <TableHead>Contato</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Canal</TableHead>
            <TableHead>Data de Criação</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.map(contact => (
            <TableRow key={contact.id}>
              <TableCell>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={selectedContacts.includes(contact.id)}
                  onChange={e => onSelectContact(contact, e.target.checked)}
                />
              </TableCell>
              <TableCell>{contact.contact}</TableCell>
              <TableCell>
                {contact.name || '-'}
                {contact.is_custom_name && contact.name && (
                  <span className="ml-2 text-xs text-gray-500">(Personalizado)</span>
                )}
              </TableCell>
              <TableCell>{getChannelBadge(contact.channel)}</TableCell>
              <TableCell>{new Date(contact.created_at).toLocaleDateString()}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(contact)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(contact)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ContactList;
